import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ResponsiveSwatchPropValue, SwatchColorOption } from './types';
import { SwatchColorCarouselProps } from './types';

const DEFAULT_COERCED_VALUES = [1, 1, 1] as number[];
const ELEMENT_PADDING = 8;

export const coerceValuestoArray = (values?: ResponsiveSwatchPropValue<number>) => {
  if (!values) {
    return DEFAULT_COERCED_VALUES;
  }

  if (Array.isArray(values)) {
    const resultValue: number[] = [];

    DEFAULT_COERCED_VALUES.forEach((_, index) => {
      const value = values[index];
      if (value === null) {
        let arrayItem: number = values[0];
        if (index === 0) {
          arrayItem = 1;
        } else {
          for (let i = 0; i < index; i++) {
            const maybePrevNumber = values[index - i];

            if (typeof maybePrevNumber === 'number') {
              arrayItem = maybePrevNumber;
              break;
            }
          }
        }
        resultValue.push(arrayItem);
      } else {
        resultValue.push(value);
      }
    });
    return resultValue;
  }

  const resultValue = DEFAULT_COERCED_VALUES.map(x => Math.max(1, values));
  return resultValue;
};

export const useSwatchColorCarousel = ({
  id,
  value,
  options,
  swatchSize,
  onSwatchClick,
  interactionType,
  onSwatchHover,
  onSwatchOut,
  onInteract,
  checkOptionAvailability
}: SwatchColorCarouselProps) => {
  swatchSize = swatchSize || 24;

  interactionType = interactionType || 'click';

  const totalColors = options?.length || 0;

  const { optionsToRender } = useMemo(() => {
    const optionsToRender = options.map(item => {
      const notAvailable = checkOptionAvailability ? !checkOptionAvailability(item.id, id) : false;
      return {
        ...item,
        notAvailable
      };
    });
    return { optionsToRender };
  }, [options, id, checkOptionAvailability]);

  const handleMouseClick = (e: React.MouseEvent, option: SwatchColorOption) => {
    if (interactionType === 'click' || interactionType === 'hover-n-click') {
      onInteract(option);
    }

    onSwatchClick && onSwatchClick(e, option);
  };

  const handleMouseOver = (e: React.MouseEvent, option: SwatchColorOption) => {
    if (interactionType === 'hover' || interactionType === 'hover-n-click') {
      onInteract(option);
    }

    onSwatchHover && onSwatchHover(e, option);
  };

  const handleMouseOut = useCallback(
    (e: React.MouseEvent, option: SwatchColorOption) => {
      onSwatchOut?.(e, option);
    },
    [onSwatchOut]
  );

  const handleKeyPress = (e: React.KeyboardEvent, option: SwatchColorOption) => {
    if (e.key === 'Enter') {
      onInteract(option);
    }
  };

  // DOM Refs
  const pageRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const elementWidth = swatchSize + ELEMENT_PADDING;
  // Get full container width
  const pageWidth = pageRef.current ? pageRef.current.clientWidth : 0;
  // Get number of columns based on pageWidth
  const columnCount = Math.floor(pageWidth / elementWidth);
  // Recalculate root width based on swatchSize
  const rootWidth = columnCount * elementWidth;

  // Settings
  const [carouselSettings, setCarouselSettings] = useState<{ currentStartIndex: number; nextPos: number; showLeftArrow: boolean; showRightArrow: boolean }>({
    currentStartIndex: 0,
    nextPos: 0,
    showLeftArrow: false,
    showRightArrow: false
  });
  // React when pageRef width is set
  useEffect(() => setCarouselSettings(curr => ({ ...curr, showRightArrow: totalColors > columnCount })), [totalColors, columnCount]);

  const handleArrowClick = (dir: 'left' | 'right') => {
    setCarouselSettings(curr => {
      let movingColors = columnCount;
      if (curr.showLeftArrow) movingColors--;
      if (curr.showRightArrow) movingColors--;
      if (dir === 'left' && columnCount - curr.currentStartIndex >= 0 && columnCount - curr.currentStartIndex < 1) movingColors++;
      if (dir === 'right' && totalColors - curr.currentStartIndex >= 0 && totalColors - curr.currentStartIndex < 1) movingColors++;

      if (dir === 'left' && curr.currentStartIndex > 0) {
        const nextStartIndex = curr.currentStartIndex - movingColors <= 1 ? 0 : curr.currentStartIndex - movingColors;
        const showLeftArrow = nextStartIndex > 0;
        const showRightArrow = nextStartIndex + columnCount < totalColors;
        if (showRightArrow && !curr.showRightArrow) {
          movingColors--;
        }
        const nextPos = curr.nextPos + movingColors * elementWidth;
        return {
          nextPos,
          currentStartIndex: nextStartIndex,
          showLeftArrow,
          showRightArrow
        };
      }

      if (dir === 'right' && curr.currentStartIndex < totalColors) {
        const nextStartIndex = curr.currentStartIndex + movingColors;
        const showLeftArrow = nextStartIndex > 0;
        const showRightArrow = nextStartIndex + columnCount < totalColors + 1;
        if (showLeftArrow && !curr.showLeftArrow) movingColors--;
        const nextPos = curr.nextPos - movingColors * elementWidth;
        return {
          nextPos,
          currentStartIndex: nextStartIndex,
          showLeftArrow,
          showRightArrow
        };
      }
      return curr;
    });
  };

  return {
    optionsToRender,
    colorSelected: value,
    swatchSize,
    handleMouseClick,
    handleMouseOver,
    handleMouseOut,
    handleKeyPress,
    handleArrowClick,
    pageRef,
    containerRef,
    nextPos: carouselSettings.nextPos,
    showLeftArrow: carouselSettings.showLeftArrow,
    showRightArrow: carouselSettings.showRightArrow,
    rootWidth
  };
};
